import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "../services/api.ts";
import toast from "react-hot-toast";

const DB_URI = process.env.REACT_APP_DB_URI;
const PF_URI = process.env.REACT_APP_PF;

// --- API Functions ---

const fetchRole = async (userId: string | number) => {
    if (!userId) return null;
    const { data } = await api.get(`${PF_URI}api/v1/users/profile/profile-by-user/${userId}`);
    return data;
};

const fetchOrganizationDocuments = async (id: string | number, userType: "user" | "organization") => {
    if (!id) return [];
    const { data } = await api.get(`${DB_URI}/users/organisation/retrieve-organization-documents/?${userType === "user" ? "user_id" : "organization_profile_id"}=${id}`);
    return data;
};

const uploadOrganizationDocument = async ({ formData, onUploadProgress }: { formData: FormData, onUploadProgress?: (progressEvent: any) => void }) => {
    const { data } = await api.post(`${DB_URI}/users/organisation/`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress
    });
    return data;
};

const deleteOrganizationDocument = async (id: number) => {
    const { data } = await api.delete(`${DB_URI}/users/organisation/${id}/`);
    return data;
};

const verifyOrganization = async () => {
    const { data } = await api.post(`${DB_URI}/users/organisation/request-verification/`, {});
    return data;
};

const fetchUserQueries = async (userId: string | number) => {
    if (!userId) return [];
    const { data } = await api.get(`${DB_URI}/users/query/user-queries/${userId}/`);
    return data;
};

const addUserQuery = async ({ token, data }: { token?: string, data: FormData }) => {
    // If token is passed, we might need to use it. api interceptor uses stored token.
    // If specific token is needed (e.g. from context not storage), we'd need config.
    // But services/Users.ts logic was weird. Assuming standard auth.
    const config = token ? { headers: { Authorization: `Bearer ${token}` } } : {};
    const { data: responseData } = await api.post(`${DB_URI}/users/query/`, data, config);
    return responseData;
};

const fetchUserProfile = async (userId: string | number) => {
    if (!userId) return null;
    const { data } = await api.get(`${DB_URI}/users/profile/profile-by-user/${userId}/`);
    return data;
};

// --- Hooks ---

export const useRole = (userId: string | number) => {
    return useQuery({
        queryKey: ['userRole', userId],
        queryFn: () => fetchRole(userId),
        enabled: !!userId,
        retry: 1
    });
};

export const useOrganizationDocuments = (id: string | number, userType: "user" | "organization" = "user") => {
    return useQuery({
        queryKey: ['organizationDocuments', id, userType],
        queryFn: () => fetchOrganizationDocuments(id, userType),
        enabled: !!id
    });
};

export const useUploadDocument = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: uploadOrganizationDocument,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['organizationDocuments'] });
            toast.success("Document uploaded successfully");
        },
        onError: (error: any) => {
            toast.error(error?.response?.data?.message || "Failed to upload document");
        }
    });
};

export const useDeleteDocument = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: deleteOrganizationDocument,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['organizationDocuments'] });
            toast.success("Document deleted successfully");
        },
        onError: (error: any) => {
            toast.error(error?.response?.data?.message || "Failed to delete document");
        }
    });
};

export const useVerifyOrganization = () => {
    return useMutation({
        mutationFn: verifyOrganization,
        onSuccess: () => {
            toast.success("Verification request sent successfully!");
        },
        onError: (error: any) => {
            toast.error("Failed to send verification request");
        }
    });
};

export const useUserQueries = (userId: string | number) => {
    return useQuery({
        queryKey: ['userQueries', userId],
        queryFn: () => fetchUserQueries(userId),
        enabled: !!userId
    });
};

export const useAddUserQuery = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: addUserQuery,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['userQueries'] });
            toast.success("Query submitted successfully");
        },
        onError: (error: any) => {
            toast.error("Failed to submit query");
        }
    });
};

export const useUserProfile = (userId: string | number) => {
    return useQuery({
        queryKey: ['userProfile', userId],
        queryFn: () => fetchUserProfile(userId),
        enabled: !!userId
    });
};
